import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Grid,
  useTheme,
  InputAdornment,
  alpha,
  CircularProgress,
  Chip,
  MenuItem,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip as MuiTooltip,
  Paper,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  Description as DescriptionIcon,
  CheckCircle as ApprovedIcon,
  Pending as PendingIcon,
  WorkHistory as TotalIcon,
  Business as OpportunityIcon,
  CheckCircleOutline as ApprovedOutlineIcon,
  ErrorOutline as RejectedIcon,
  Launch as ViewIcon,
} from '@mui/icons-material';
import { formInputStyles, formButtonStyles, numberInputProps } from '@/styles/formStyles';
import { VolunteerApi, VolunteerOpportunity, VolunteerLogEntry } from '@/services/api/domains/volunteer';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useToast } from '@/components/common/Toast';
import { format } from 'date-fns';
import { VolunteerLogModal } from '@/components/volunteer/VolunteerLogModal';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

export const VolunteerHoursPage = () => {
  const theme = useTheme();
  const toast = useToast();
  const [hours, setHours] = useState('');
  const [hoursError, setHoursError] = useState('');
  const [hoursFieldTouched, setHoursFieldTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [opportunityId, setOpportunityId] = useState('');
  const [opportunityError, setOpportunityError] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [metrics, setMetrics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [opportunities, setOpportunities] = useState<VolunteerOpportunity[]>([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState<VolunteerOpportunity | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedLog, setSelectedLog] = useState<VolunteerLogEntry | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchVolunteerData();
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    try {
      const volunteerApi = new VolunteerApi();
      const response = await volunteerApi.getAvailableOpportunities();
      setOpportunities(response.data);
    } catch (error) {
      console.error('Failed to fetch opportunities:', error);
      toast.showError(error);
    }
  };

  const fetchVolunteerData = async () => {
    try {
      const volunteerApi = new VolunteerApi();
      const response = await volunteerApi.getLogs();
      setMetrics(response.data);
    } catch (error) {
      console.error('Failed to fetch volunteer data:', error);
      toast.showError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpportunityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setOpportunityId(value);
    setOpportunityError(''); // Clear opportunity error
    setHoursError(''); // Clear hours error when opportunity changes
    if (value) {
      const id = Number(value);
      const found = opportunities.find(opp => opp.id === id);
      setSelectedOpportunity(found || null);
    } else {
      setSelectedOpportunity(null);
    }
    setHours(''); // Reset hours when opportunity changes
  };

  const validateHours = (value: string): boolean => {
    if (!value) {
      setHoursError('Please enter the number of hours');
      return false;
    }
    if (!/^\d*\.?\d*$/.test(value)) {
      setHoursError('Please enter a valid number');
      return false;
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue <= 0) {
      setHoursError('Hours must be greater than 0');
      return false;
    }
    if (selectedOpportunity && numValue > selectedOpportunity.hours_remaining) {
      setHoursError(`You can only log up to ${selectedOpportunity.hours_remaining} hours for this opportunity`);
      return false;
    }
    setHoursError('');
    return true;
  };

  const handleHoursChange = (value: string) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setHours(value);
      if (opportunityId && hoursFieldTouched && value === '') {
        setHoursError('Please enter the number of hours');
      } else {
        setHoursError('');
      }
    }
  };

  const handleHoursBlur = () => {
    setHoursFieldTouched(true);
    if (opportunityId && hours === '') {
      setHoursError('Please enter the number of hours');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const isHoursValid = validateHours(hours);
    if (!opportunityId) {
      setOpportunityError('Please select an opportunity');
      return;
    }
    if (!isHoursValid) {
      return;
    }

    try {
      setIsPending(true);
      const volunteerApi = new VolunteerApi();
      await volunteerApi.create({
        opportunity_id: Number(opportunityId),
        hours: parseFloat(hours),
        description,
        date: new Date().toISOString(),
      });
      toast.showSuccess('Hours submitted successfully!');
      setHours('');
      setHoursError('');
      setDescription('');
      setOpportunityId('');
      setOpportunityError('');
      setSelectedOpportunity(null);
      fetchVolunteerData();
      fetchOpportunities();
    } catch (error) {
      console.error('Failed to submit hours:', error);
      toast.showError(error);
    } finally {
      setIsPending(false);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return (
          <Chip
            icon={<ApprovedOutlineIcon />}
            label="Approved"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.success.main, 0.1),
              color: theme.palette.success.main,
              '& .MuiChip-icon': {
                color: theme.palette.success.main,
              },
            }}
          />
        );
      case 'pending':
        return (
          <Chip
            icon={<PendingIcon />}
            label="Pending"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              color: theme.palette.warning.main,
              '& .MuiChip-icon': {
                color: theme.palette.warning.main,
              },
            }}
          />
        );
      case 'rejected':
        return (
          <Chip
            icon={<RejectedIcon />}
            label="Rejected"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
              '& .MuiChip-icon': {
                color: theme.palette.error.main,
              },
            }}
          />
        );
      default:
        return null;
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewLog = (log: VolunteerLogEntry) => {
    setSelectedLog(log);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedLog(null);
    setIsModalOpen(false);
  };

  const handleUpdateLog = async (logId: number, hours: number, description: string) => {
    try {
      const volunteerApi = new VolunteerApi();
      await volunteerApi.update(logId, { hours, description });
      await fetchVolunteerData(); // Refresh data after update
    } catch (error) {
      throw error; // Let the modal handle the error
    }
  };

  const handleDeleteLog = async (logId: number) => {
    try {
      const volunteerApi = new VolunteerApi();
      await volunteerApi.delete(logId);
      await fetchVolunteerData(); // Refresh data after deletion
    } catch (error) {
      throw error; // Let the modal handle the error
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold', color: theme.palette.primary.main }}>
        Volunteer Dashboard
      </Typography>
      
      <Grid container spacing={4}>
        {/* Summary Cards */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                background: alpha(theme.palette.primary.main, 0.1),
                borderRadius: 2,
                height: '100%'
              }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <TotalIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                    <Box>
                      <Typography variant="h6">Total Hours</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {metrics?.metrics?.total_hours || 0}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                background: alpha(theme.palette.success.main, 0.1),
                borderRadius: 2,
                height: '100%'
              }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <ApprovedIcon sx={{ fontSize: 40, color: theme.palette.success.main }} />
                    <Box>
                      <Typography variant="h6">Approved Hours</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {metrics?.metrics?.approved_hours || 0}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                background: alpha(theme.palette.warning.main, 0.1),
                borderRadius: 2,
                height: '100%'
              }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <PendingIcon sx={{ fontSize: 40, color: theme.palette.warning.main }} />
                    <Box>
                      <Typography variant="h6">Pending Hours</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {metrics?.metrics?.pending_hours || 0}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Charts Section */}
        <Grid item xs={12} md={8}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 4,
            background: alpha(theme.palette.background.paper, 0.8),
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Industry Breakdown</Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={metrics?.metrics?.industry_breakdown || []}>
                    <XAxis dataKey="industry" />
                    <YAxis />
                    <Tooltip />
                    <Bar 
                      dataKey="hours" 
                      fill={theme.palette.primary.main}
                      maxBarSize={100}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Submit Hours Form */}
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            height: '100%',
            borderRadius: 4,
            background: alpha(theme.palette.background.paper, 0.8),
            ...formInputStyles(theme),
          }}>
            <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
              <Typography variant="h6" color="primary" fontWeight="bold" sx={{ mb: 3 }}>
                Submit Hours
              </Typography>
              <Box 
                component="form" 
                onSubmit={handleSubmit}
                noValidate // Disable browser validation
              >
                <Stack spacing={3}>
                  <TextField
                    select
                    fullWidth
                    label="Opportunity"
                    placeholder="Select an opportunity"
                    value={opportunityId}
                    onChange={handleOpportunityChange}
                    required
                    error={!!opportunityError}
                    helperText={opportunityError}
                    SelectProps={{
                      displayEmpty: true,
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <OpportunityIcon color={opportunityError ? "error" : "action"} />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="">
                      <em>Select an opportunity</em>
                    </MenuItem>
                    {opportunities.map((opp) => (
                      <MenuItem key={opp.id} value={String(opp.id)}>
                        {opp.title} - {opp.organization}
                      </MenuItem>
                    ))}
                  </TextField>
                  {selectedOpportunity && (
                    <FormHelperText>
                      {selectedOpportunity.hours_remaining} hours remaining out of {selectedOpportunity.commitment_hours} total hours
                    </FormHelperText>
                  )}
                  <TextField
                    fullWidth
                    type="number"
                    label="Hours"
                    value={hours}
                    onChange={(e) => handleHoursChange(e.target.value)}
                    onBlur={handleHoursBlur}
                    error={!!hoursError}
                    helperText={hoursError}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TimeIcon color={hoursError ? "error" : "action"} />
                        </InputAdornment>
                      ),
                      inputProps: {
                        ...numberInputProps,
                        min: 0,
                        step: 0.5
                      }
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DescriptionIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isPending}
                    size="large"
                    sx={formButtonStyles(theme)}
                  >
                    {isPending ? 'Submitting...' : 'Submit Hours'}
                  </Button>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Skills Section */}
        <Grid item xs={12}>
          <Card sx={{ 
            borderRadius: 4,
            background: alpha(theme.palette.background.paper, 0.8),
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Skills Gained</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {metrics?.metrics?.skills_breakdown?.map((skill: any, index: number) => (
                  <Chip
                    key={skill.skill}
                    label={`${skill.skill} (${skill.count})`}
                    sx={{
                      bgcolor: alpha(COLORS[index % COLORS.length], 0.1),
                      color: COLORS[index % COLORS.length],
                      fontWeight: 'medium',
                    }}
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Volunteer Logs Table */}
        <Grid item xs={12}>
          <Card sx={{ 
            borderRadius: 4,
            background: alpha(theme.palette.background.paper, 0.8),
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>Recent Volunteer Logs</Typography>
              <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Opportunity</TableCell>
                      <TableCell>Organization</TableCell>
                      <TableCell>Hours</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(metrics?.logs || [])
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((log: VolunteerLogEntry) => (
                        <TableRow key={log.id} hover>
                          <TableCell>
                            {format(new Date(log.date), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell>{log.opportunity.title}</TableCell>
                          <TableCell>{log.opportunity.organization}</TableCell>
                          <TableCell>{log.hours}</TableCell>
                          <TableCell>{getStatusChip(log.status)}</TableCell>
                          <TableCell align="right">
                            <MuiTooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewLog(log)}
                                sx={{ 
                                  color: theme.palette.primary.main,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  },
                                }}
                              >
                                <ViewIcon />
                              </IconButton>
                            </MuiTooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={metrics?.logs?.length || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <VolunteerLogModal
        open={isModalOpen}
        onClose={handleCloseModal}
        log={selectedLog}
        onUpdate={handleUpdateLog}
        onDelete={handleDeleteLog}
        onSuccess={fetchVolunteerData}
      />
    </Box>
  );
}; 